import { useState } from 'react'
import { SearchFilters } from '../types/property'
import FiltersModal from './FiltersModal'

interface FilterBarProps {
  filters: SearchFilters
  onChange: (filters: SearchFilters) => void
  onSaveSearch: () => void
  resultsCount: number
}

export default function FilterBar({ filters, onChange, onSaveSearch, resultsCount }: FilterBarProps) {
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [searchInput, setSearchInput] = useState(filters.q || '')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    onChange({ ...filters, q: searchInput })
  }

  const statusOptions = ['Active', 'Pending', 'Coming Soon']
  const bedsOptions = [1, 2, 3, 4, 5]
  const bathsOptions = [1, 2, 3, 4]

  const priceRanges = [
    { label: 'Any', value: undefined },
    { label: '$100k', value: 100000 },
    { label: '$200k', value: 200000 },
    { label: '$300k', value: 300000 },
    { label: '$400k', value: 400000 },
    { label: '$500k', value: 500000 },
    { label: '$750k', value: 750000 },
    { label: '$1M', value: 1000000 },
    { label: '$1.5M', value: 1500000 },
    { label: '$2M', value: 2000000 },
  ]

  return (
    <>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        {/* Search Bar */}
        <div className="px-4 py-3 border-b border-gray-100">
          <form onSubmit={handleSearch} className="max-w-4xl mx-auto">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by city, zip, or address..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
              <button
                type="submit"
                className="px-6 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Quick Filters */}
        <div className="px-4 py-3 flex items-center gap-3 overflow-x-auto">
          {/* Status */}
          <select
            value={filters.status?.[0] || ''}
            onChange={(e) => onChange({ ...filters, status: e.target.value ? [e.target.value] : undefined })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">Status</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          {/* Min Price */}
          <select
            value={filters.minPrice || ''}
            onChange={(e) => onChange({ ...filters, minPrice: e.target.value ? Number(e.target.value) : undefined })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">Min Price</option>
            {priceRanges.map(range => (
              <option key={range.label} value={range.value}>{range.label}</option>
            ))}
          </select>

          {/* Max Price */}
          <select
            value={filters.maxPrice || ''}
            onChange={(e) => onChange({ ...filters, maxPrice: e.target.value ? Number(e.target.value) : undefined })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">Max Price</option>
            {priceRanges.map(range => (
              <option key={range.label} value={range.value}>{range.label}</option>
            ))}
          </select>

          {/* Beds */}
          <select
            value={filters.beds || ''}
            onChange={(e) => onChange({ ...filters, beds: e.target.value ? Number(e.target.value) : undefined })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">Beds</option>
            {bedsOptions.map(beds => (
              <option key={beds} value={beds}>{beds}+ Beds</option>
            ))}
          </select>

          {/* Baths */}
          <select
            value={filters.baths || ''}
            onChange={(e) => onChange({ ...filters, baths: e.target.value ? Number(e.target.value) : undefined })}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">Baths</option>
            {bathsOptions.map(baths => (
              <option key={baths} value={baths}>{baths}+ Baths</option>
            ))}
          </select>

          {/* More Filters Button */}
          <button
            onClick={() => setShowFiltersModal(true)}
            className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition whitespace-nowrap"
          >
            More Filters
          </button>

          {/* Save Search */}
          <button
            onClick={onSaveSearch}
            className="px-4 py-1.5 border border-black rounded-lg text-sm font-medium hover:bg-black hover:text-white transition whitespace-nowrap ml-auto"
          >
            Save Search
          </button>
        </div>

        {/* Results Count */}
        <div className="px-4 py-2 text-sm text-gray-600 bg-gray-50">
          {resultsCount.toLocaleString()} properties found
        </div>
      </div>

      {showFiltersModal && (
        <FiltersModal
          filters={filters}
          onClose={() => setShowFiltersModal(false)}
          onApply={(newFilters) => {
            onChange(newFilters)
            setShowFiltersModal(false)
          }}
        />
      )}
    </>
  )
}
